import React, { useMemo, useState } from 'react'
import { Button, Card, Spin, Typography } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiUrl } from '../config/api'
import useEmblaCarousel from 'embla-carousel-react'
import ProductCard from '../components/ProductCard.jsx'
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'

export default function UnifiedCatalog() {
    const navigate = useNavigate()
    const { categoryId } = useParams()

    const raw = localStorage.getItem('deliveryLocation')
    let deliveryId = null
    if (raw) {
        try {
            const obj = JSON.parse(raw)
            if (obj && typeof obj === 'object') deliveryId = obj.id ?? null
        } catch {
            // ignore
        }
    }

    const enabled = !!categoryId && !!deliveryId

    // Fetch grouped items for this main category
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['unifiedCatalog', categoryId, deliveryId],
        enabled,
        queryFn: async () => {
            const url = apiUrl(
                `/api/main-categories/${encodeURIComponent(categoryId)}/items?deliveryLocationId=${encodeURIComponent(deliveryId)}&byTags=true`
            )
            const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
            if (!res.ok) throw new Error(`فشل في جلب البيانات (${res.status})`)
            return res.json()
        }
    })

    // Fetch category info for header
    const { data: categoryData } = useQuery({
        queryKey: ['mainCategory', categoryId],
        enabled: !!categoryId,
        queryFn: async () => {
            const url = apiUrl(`/api/main-categories?deliveryLocationId=${encodeURIComponent(deliveryId || 0)}`)
            const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
            if (!res.ok) return null
            const list = await res.json()
            return Array.isArray(list) ? list.find(c => String(c.id) === String(categoryId)) : null
        }
    })

    const sections = useMemo(() => {
        if (!data || typeof data !== 'object' || Array.isArray(data)) return []
        return Object.entries(data)
            .filter(([, arr]) => Array.isArray(arr) && arr.length > 0)
            .map(([key, arr]) => ({ key, items: arr }))
    }, [data])

    const headerTitle = categoryData?.tagAr || ''
    const headerImageUrl = categoryData?.imageUrl

    return (
        <div style={{ display: 'grid', gap: 'clamp(12px, 3vw, 20px)', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 16, right: 16 }}>
                <Button onClick={() => navigate('/categories')}>عودة</Button>
            </div>

            {/* Header */}
            {(headerTitle || headerImageUrl) && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    padding: '16px 0 12px'
                }}>
                    {headerImageUrl && (
                        <div style={{
                            width: 48,
                            height: 48,
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: '#fafafa',
                            flexShrink: 0
                        }}>
                            <img
                                src={`https://images.leen-go.com/${headerImageUrl}`}
                                alt={headerTitle}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                loading="lazy"
                                onError={(e) => {
                                    if (!e.currentTarget.src.endsWith('/no-image.png')) e.currentTarget.src = '/no-image.png'
                                }}
                            />
                        </div>
                    )}
                    {headerTitle && (
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            {headerTitle}
                        </Typography.Title>
                    )}
                </div>
            )}

            {!enabled && (
                <Card>
                    <Typography.Paragraph style={{ margin: 0 }}>
                        بيانات غير مكتملة.
                    </Typography.Paragraph>
                </Card>
            )}

            {enabled && isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                    <Spin />
                </div>
            )}

            {enabled && isError && (
                <Card>
                    <Typography.Text type="danger" style={{ marginBottom: 12, display: 'block' }}>
                        حدث خطأ: {error?.message || 'خطأ غير معروف'}
                    </Typography.Text>
                    <Button onClick={() => refetch()}>إعادة المحاولة</Button>
                </Card>
            )}

            {enabled && !isLoading && !isError && sections.length === 0 && (
                <Card>
                    <Typography.Paragraph style={{ margin: 0 }}>
                        لا توجد منتجات متاحة حالياً.
                    </Typography.Paragraph>
                </Card>
            )}

            {enabled && !isLoading && !isError && sections.map((section) => (
                <SectionCarousel key={section.key} title={section.key} items={section.items} />
            ))}
        </div>
    )
}

function SectionCarousel({ title, items }) {
    const [expanded, setExpanded] = useState(false)
    const [emblaRef] = useEmblaCarousel({ align: 'start', dragFree: true, containScroll: 'trimSnaps', direction: 'rtl' })

    const toggle = () => setExpanded(prev => !prev)

    return (
        <div style={{ display: 'grid', gap: 12, position: 'relative', background: 'white', borderRadius: 20, padding: '15px 5px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Title
                    level={4}
                    style={{ margin: 0, cursor: 'pointer', userSelect: 'none' }}
                    onClick={toggle}
                >
                    {title}
                </Typography.Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {!expanded && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            اسحب للمزيد ⬅️ أو
                        </Typography.Text>
                    )}
                    <Button
                        size="small"
                        onClick={toggle}
                        icon={expanded ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    >
                        {expanded ? 'إخفاء' : 'مشاهدة الكل'}
                    </Button>
                </div>
            </div>

            {!expanded ? (
                <div ref={emblaRef} dir="rtl" style={{ overflow: 'hidden', touchAction: 'pan-y' }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
                        {items.map((priced, idx) => (
                            <div
                                key={priced?.id || idx}
                                style={{ flex: '0 0 auto', width: 'min(42vw, 132px)', display: 'flex' }}
                            >
                                <ProductCard pricedItem={priced} />
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div
                    dir="rtl"
                    className="expanded-items-container"
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 12,
                        alignItems: 'stretch',
                        alignContent: 'flex-start',
                        width: '100%',
                        position: 'relative',
                        overflow: 'visible'
                    }}
                >
                    <style>
                        {`
                        @media (max-width: 576px) {
                            .expanded-items-container { gap: 12px 4% !important; }
                            .expanded-item-wrapper { width: 48% !important; flex: 0 0 48% !important; }
                        }
                        `}
                    </style>
                    {items.map((priced, idx) => (
                        <div
                            key={priced?.id || idx}
                            className="expanded-item-wrapper"
                            style={{ flex: '0 0 auto', width: 'min(42vw, 132px)', display: 'flex' }}
                        >
                            <ProductCard pricedItem={priced} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
